import { useId } from "react";
import { ChevronDown } from "lucide-react";

import { strings } from "@perceptron/constants";
import { Button } from "@perceptron/ui/components/button";
import { cn } from "@perceptron/utils/cn";

import type { TrackingMethod } from "../contracts";
import { Checkbox } from "./Checkbox";
import { sameInspectorSettings, useTrackingInspectorStore, type InspectorSettings } from "./controller";

const copy = strings.trackingInspector;
const STRATEGIES: { key: TrackingMethod; label: string }[] = [
  { key: "sparse", label: copy.sparse },
  { key: "nano", label: copy.nano },
  { key: "hybrid", label: copy.hybrid },
];

function describeSettings(settings: InspectorSettings): string {
  if (!settings.interpolationEnabled) return copy.interpolationOff;
  const sources = STRATEGIES.filter(({ key }) => settings.strategies[key]).map(({ label }) => label);
  if (!sources.length) return copy.noStrategies;
  const schedule =
    settings.strategies.nano || settings.strategies.hybrid
      ? ` · ${settings.nanoMode === "auto" ? copy.automatic : copy.everyInterval}`
      : "";
  return `${sources.join(", ")}${schedule}`;
}

export function StrategiesPanel() {
  const active = useTrackingInspectorStore((state) => state.activeSettings);
  const draft = useTrackingInspectorStore((state) => state.draftSettings);
  const updateDraft = useTrackingInspectorStore((state) => state.updateDraft);
  const recovery = useTrackingInspectorStore((state) => state.summary?.recovery);
  const radioName = useId();
  const changed = !sameInspectorSettings(active, draft);
  const hasStrategies = Object.values(draft.strategies).some(Boolean);
  return (
    <div className="space-y-5">
      <p className="flex items-center gap-2 text-xs text-foreground-secondary" role="status">
        <span
          aria-hidden="true"
          className={cn("size-1.5 rounded-full", changed ? "bg-brand-blue" : "bg-foreground-secondary")}
        />
        {changed ? copy.unsavedChanges : copy.savedSettings}
      </p>

      <section aria-labelledby={`${radioName}-local`} className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-x-3">
          <h4 className="font-medium" id={`${radioName}-local`}>
            {copy.localStrategies}
          </h4>
          <Checkbox
            checked={draft.interpolationEnabled}
            label={copy.interpolate}
            onChange={(interpolationEnabled) => updateDraft({ interpolationEnabled })}
          />
        </div>
        <fieldset className="disabled:opacity-50" disabled={!draft.interpolationEnabled}>
          <legend className="sr-only">{copy.outputStrategies}</legend>
          <div className="divide-y divide-border">
            {STRATEGIES.map(({ key, label }) => (
              <div className="py-1.5 first:pt-0" key={key}>
                <Checkbox
                  checked={draft.strategies[key]}
                  label={label}
                  onChange={(checked) => updateDraft({ strategies: { ...draft.strategies, [key]: checked } })}
                />
                <p className="-mt-0.5 pl-6 text-xs leading-5 text-foreground-secondary">
                  {copy.strategyDescriptions[key]}
                </p>
              </div>
            ))}
          </div>
        </fieldset>
        {!draft.interpolationEnabled && <p className="text-xs text-foreground-secondary">{copy.interpolationOff}</p>}
        {draft.interpolationEnabled && !hasStrategies && (
          <p className="text-xs text-foreground-secondary">{copy.noStrategies}</p>
        )}
        <details className="text-xs text-foreground-secondary">
          <summary className="cursor-pointer rounded-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
            {copy.strategyHelp}
          </summary>
          <div className="space-y-2 pt-2 leading-5">
            <p>
              {copy.strategyHint} {copy.unavailableHint}
            </p>
            <p>
              <span className="font-medium text-foreground">{copy.activeSettings}: </span>
              {describeSettings(active)}
            </p>
          </div>
        </details>
      </section>

      {(draft.strategies.nano || draft.strategies.hybrid) && (
        <details className="group border-t border-border pt-3">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-2 rounded-sm text-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring [&::-webkit-details-marker]:hidden">
            <span className="font-medium">{copy.nanoScheduling}</span>
            <span className="ml-auto text-foreground-secondary">
              {draft.nanoMode === "auto" ? copy.automatic : copy.everyInterval}
            </span>
            <ChevronDown
              aria-hidden="true"
              className="size-3.5 text-foreground-secondary transition-transform group-open:rotate-180 motion-reduce:transition-none"
            />
          </summary>
          <fieldset className="mt-3 space-y-2 disabled:opacity-50" disabled={!draft.interpolationEnabled}>
            <legend className="sr-only">{copy.nanoScheduling}</legend>
            <div className="grid grid-cols-2 gap-2">
              {(["auto", "always"] as const).map((mode) => (
                <label
                  className={cn(
                    "flex min-h-10 cursor-pointer items-center gap-2 rounded-md border border-border px-3 py-2 text-xs",
                    draft.nanoMode === mode && "bg-background-alt-2 text-foreground",
                  )}
                  key={mode}
                >
                  <input
                    checked={draft.nanoMode === mode}
                    className="size-3.5 shrink-0 accent-brand-blue focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed"
                    name={radioName}
                    type="radio"
                    value={mode}
                    onChange={() => updateDraft({ nanoMode: mode })}
                  />
                  {mode === "auto" ? copy.automatic : copy.everyInterval}
                </label>
              ))}
            </div>
            <p className="text-xs leading-5 text-foreground-secondary">
              {draft.nanoMode === "auto" ? copy.automaticHint : copy.everyIntervalHint}
            </p>
          </fieldset>
        </details>
      )}

      <section aria-labelledby={`${radioName}-recovery`} className="space-y-2 border-t border-border pt-4">
        <h4 className="font-medium" id={`${radioName}-recovery`}>
          {copy.recovery.title}
        </h4>
        <fieldset
          className="disabled:opacity-50"
          disabled={!recovery?.available || !draft.interpolationEnabled || !hasStrategies}
        >
          <legend className="sr-only">{copy.recovery.title}</legend>
          <Checkbox
            checked={!!draft.recoveryEnabled}
            label={copy.recovery.enable}
            onChange={(recoveryEnabled) => updateDraft({ recoveryEnabled })}
          />
        </fieldset>
        <p className="text-xs leading-5 text-foreground-secondary">
          {!recovery?.available
            ? copy.recovery.unavailable
            : !draft.interpolationEnabled || !hasStrategies
              ? copy.recovery.requiresLocal
              : copy.recovery.shortHint}
        </p>
        {recovery?.providerLabel && (
          <dl className="flex min-w-0 gap-2 text-xs">
            <dt className="shrink-0 text-foreground-secondary">{copy.recovery.model}</dt>
            <dd className="min-w-0 truncate" title={recovery.providerLabel}>
              {recovery.providerLabel}
            </dd>
          </dl>
        )}
        <p className="text-xs leading-5 text-foreground-secondary">{copy.recovery.limits}</p>
        <details className="text-xs text-foreground-secondary">
          <summary className="cursor-pointer rounded-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
            {copy.recovery.help}
          </summary>
          <div className="space-y-2 pt-2 leading-5">
            <p>{copy.recovery.hint}</p>
            <p>{copy.recovery.cacheHint}</p>
          </div>
        </details>
      </section>
    </div>
  );
}

export function StrategyActions() {
  const active = useTrackingInspectorStore((state) => state.activeSettings);
  const draft = useTrackingInspectorStore((state) => state.draftSettings);
  const apply = useTrackingInspectorStore((state) => state.apply);
  const discard = useTrackingInspectorStore((state) => state.discard);
  const resetDraft = useTrackingInspectorStore((state) => state.resetDraft);
  const changed = !sameInspectorSettings(active, draft);
  return (
    <div
      className="flex shrink-0 flex-wrap items-center gap-2 border-t border-border p-3"
      data-tracking-strategy-actions
    >
      <Button
        className="px-2 text-sm text-foreground-secondary"
        size="sm"
        type="button"
        variant="ghost"
        onClick={resetDraft}
      >
        {copy.resetDefaults}
      </Button>
      <div className="ml-auto flex gap-2">
        <Button
          className="text-sm shadow-none"
          disabled={!changed}
          size="sm"
          type="button"
          variant="outline"
          onClick={discard}
        >
          {copy.discard}
        </Button>
        <Button className="text-sm shadow-none" disabled={!changed} size="sm" type="button" onClick={apply}>
          {copy.apply}
        </Button>
      </div>
    </div>
  );
}
